import React from "react";

const PresenceVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
      This lesson reframes executive presence as a pattern of signals, not perfection or wardrobe. It focuses on how you make people feel and the steady cues you send through clarity, calm, and reliability. It connects presence to personal brand, where work plus character becomes a credibility shortcut. It highlights micro moments, pacing, pauses, and protecting time, energy, and tools as visible proof of ownership that shapes perception.
      </p>
    </div>
  );

  const transcriptHtml = `
    <p>Do you remember the last time your leader walked into the conference room and addressed your team? The way everyone paused, the room seemed to shift with her <strong>presence</strong>? And you thought, Why doesn't that happen when I walk in? That feeling of authority and impact is what we call <strong>executive presence</strong>.</p>

    <p>Many believe it's about perfection, always saying the right thing or owning a wardrobe full of navy blazers. The truth is far from it.</p>

    <p><strong>Executive presence comes from consistently signaling reliability, clarity, and authority</strong> in ways that align with your personal brand.</p>

    <p>For example, if you are known as an "innovative problem-solver," don't do it silently like a corporate ninja. Share the how in team meetings: your approach, your thought process, your insights. People notice patterns, not one-off brilliance.</p>

    <p>Sounds fancy, right? Don't worry, we're not turning this into a TED Talk on posture and power suits. Let's break it down.</p>

    <p><strong>Executive presence is emotional, not just rational.</strong> Your colleagues will not remember what you said, but they will remember how you made them feel.</p>

    <p>Make people feel confident in your abilities. Make them feel valued. Make them feel like the future is better because you're involved.</p>

    <p>Be the calm one when everyone else is losing Wi-Fi and patience. Make them feel better about the disruption, help them see the other side of the coin.</p>

    <p>And those tiny moments, how you handle interruptions, stress, or that one colleague who never reads emails, that's where your presence really shows up.</p>

    <p><strong>Your personal brand amplifies your presence.</strong> It's your credibility shortcut, the pattern people recognize when you're not in the room.</p>

    <p>Completing your work isn't your brand. It's the combination of your work and what that work stands for.</p>

    <p>Think of it as your work plus your character, how you bring yourself into what you do. Even when you're not trying, who you are seeps into your output.</p>

    <p>If you always deliver projects on time but show up five minutes late to every meeting, then punctuality isn't your brand but reliability might be.</p>

    <p>Decide your signature "thing": problem-solving, team-building, insight-generation, and make it consistently visible in small ways:</p>
    <ul style="margin-left: 20px;">
      <li>Quick, clear emails summarizing complex ideas</li>
      <li>Subtle insights in meetings</li>
      <li>Acknowledging team wins without showing off</li>
    </ul>

    <p>It can make you stand out in the crowd and that goes a long way.</p>

    <p><strong>Executive presence is how your energy lands in a room.</strong></p>

    <p>Every meeting has a rhythm. Some people rush to fill the silence, others command attention by using it.</p>

    <p>Observe the leaders you admire, how do they pause, gesture, or react under pressure? Notice it, don't imitate.</p>

    <p>Their calm doesn't come from lack of urgency; it comes from control over their energy.</p>

    <p>Your energy signals confidence long before your words do. When you're composed, people assume you're capable. When you're frantic, people assume you're unsure, even if your ideas are great.</p>

    <p>Speak slowly, pause intentionally, and let your presence land before you speak.</p>

    <p><strong>Promotions depend on two things majorly: visibility and perception.</strong> Small, repeated signals matter more than heroics.</p>

    <p>Protect your reputation, avoid gossip, but also don't disappear from decision-makers' radar. Your brand should be present, not pushy.</p>

    <p>So, does my appearance matter?</p>

    <p>We often think good-looking people get promoted faster, but it's not really about their looks. It's what their appearance signals. Someone who puts effort into looking presentable sends a simple message: <strong>I take care of myself.</strong> And such people are often trusted to take care of their work too.</p>

    <p>You don't need designer clothes or a "polished look" to send that signal. You can project the same confidence by showing you protect what's important to you.</p>

    <p>Protect your time: keep your calendar and timelines disciplined.</p>
    <p>Protect your energy: your lunch hour is sacred, set healthy boundaries.</p>
    <p>Protect your tools: whether it's your laptop, your workspace, or even how you organize your notes.</p>

    <p>These micro-signals tell the world: you're dependable, you're intentional, you take ownership.</p>

    <p><strong>Confidence, clarity, and consistency matter far more than the label on your shirt.</strong></p>

    <p>Executive presence is consistency. The signals you send, the way your energy lands, the small repeated patterns, these define how people see you.</p>

    <p>This week, focus on how you show up in every interaction, meetings, emails, conversations.</p>

    <p><strong>What are the signals you're sending?</strong></p>
  `;

  const transcript = (
    <div
      className="pt-2 pb-6 text-body leading-7 text-[#484848] max-w-2xl"
      dangerouslySetInnerHTML={{ __html: transcriptHtml }}
    />
  );

  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default PresenceVideo;

